import React, { useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { SceneWithUserNames, SceneInteriorExterior, SceneDayNight } from '../../../types/scene.types';
import { ScenesTableContainerProps } from './types';
import { useSceneCellEdit } from './Hooks/useSceneCellEdit';
import { useFindReplace } from './Hooks/UseFindReplace';
import { useFloatingComponentPosition } from './Hooks/UseFloatingComponentPosition';
import { useSceneCodeValidation } from './Hooks/UseSceneCodeValidation';
import { sortScenes } from '../BreakdownMain/Utils/SceneCodeUtils';
import FindReplace from '../../../components/FindReplace';
import { useBreakdownTabContext } from '../../../contexts/BreakdownTabContext';
import { useCurrentUser } from '../../../contexts/CurrentUserContext';
import { TableColumn, TableCallbacks, CellPosition } from './types/excelTableTypes';
import { TableCell, type TableCellInputRecommendConfig } from './components/TableCell';
import { UnifiedPosition } from '../shortcut/useUnifiedNavigationState';
// 注意：useTableState 和 useKeyboardNavigation 已移除，现在使用统一导航系统
import { getSceneCodeCellBackgroundColor } from './Utils/SceneCellUtils';
import { ReactComponent as SortIcon } from '../../../assets/svg/Breakdown/sort.svg';
import { ReactComponent as FindReplaceIcon } from '../../../assets/svg/Breakdown/findreplace.svg';
import { ReactComponent as AddIcon } from '../../../assets/svg/Breakdown/add.svg';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import { MenuPosition } from '../../../components/DropdownMenu/useDropdownMenuField';
import './ScenesTable.css';
import './ExcelTable.css';

// 注意：以下导入仅用于查找替换功能，不再用于表格渲染
// import { ReactComponent as SortIcon } from '../../../assets/svg/Breakdown/sort.svg';
// import { ReactComponent as AddIcon } from '../../../assets/svg/Breakdown/add.svg';
// import { ReactComponent as FindReplaceIcon } from '../../../assets/svg/Breakdown/findreplace.svg';
// import InputRecommendNEW from '../../../components/InputRecommend/InputRecommendNEW';
// import { SceneInteriorExteriorField, SceneDayNightField } from '../../../components/DropdownMenu';
import { formatPageNumberDisplay } from '../../../utils/pageNumberUtils';
// import { getSceneCodeCellBackgroundColor } from './Utils/SceneCellUtils';

// 表格列类型定义
export interface ScenesTableColumn<Row> {
  key: string;
  title?: React.ReactNode;
  width?: number;
  className?: string;
  dataIndex?: keyof Row & string;
  render?: (value: any, record: Row, rowIndex: number) => React.ReactNode;
  sortable?: boolean; // 是否可排序
  sortType?: 'sceneCode' | 'text'; // 排序类型
}

// 注意：旧的 ScenesTableHeader 和 ScenesTableBody 组件已移除
// 现在使用 ExcelTable 组件进行渲染
// 以下类型定义保留用于查找替换功能

const ScenesTable: React.FC<ScenesTableContainerProps> = ({
  scenes,
  selectedSceneId,
  setSelectedSceneId,
  setSelectedSceneData,
  sortConfig,
  setSortConfig,
  sceneLocationDisplayTexts,
  setSceneLocationDisplayTexts,
  sceneSetDisplayTexts,
  setSceneSetDisplayTexts,
  setSearchVisible,
  setSetSearchVisible,
  setSearchText,
  setSetSearchText,
  activeSceneId,
  setActiveSceneId,
  setSetSearchPosition,
  setLocalSceneSetDisplay,
  setLocalSceneLocationDisplay,
  sceneLocationSearchVisible,
  setSceneLocationSearchVisible,
  sceneLocationSearchText,
  setSceneLocationSearchText,
  activeLocationSceneId,
  setActiveLocationSceneId,
  setSceneLocationSearchPosition,
  handleSceneUpdate,
  flushSceneUpdate,
  handleAddSceneInTable,
  handleAddSceneInTableEnd,
  breakdownId: breakdownIdProp,
  canEdit = false,
  setScenes,
  onClearOtherStates,
  onRegisterClearState,
  // 统一导航系统
  currentStateAndPosition,
  getPositionState,
  setSelection,
  scenesTableElementRefs,
}) => {
  const { t } = useTranslation();
  const { breakdownId: contextBreakdownId } = useBreakdownTabContext();
  const effectiveBreakdownId = breakdownIdProp || contextBreakdownId;
  const { setLocalOnlyField } = useCurrentUser();

  const scenesRef = useRef<SceneWithUserNames[]>(scenes);
  useEffect(() => {
    scenesRef.current = scenes;
  }, [scenes]);

  /** 与排序后一致：将带 .breakdown-row-selected 的行滚到可视区垂直居中 */
  const scrollSelectedScenesRowIntoView = useCallback(() => {
    window.setTimeout(() => {
      const row = document.querySelector('tr.breakdown-row-selected');
      row?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, 0);
  }, []);

  /** 避免同一 `分场Id:场次Id` 在 scenes 引用频繁变时重复滚动；换分场后 key 含 breakdownId，会重新滚一次 */
  const lastScrolledSelectionKeyRef = useRef<string | null>(null);
  /** 场号编辑完成后，等待排序/ID 替换稳定后将场号单元格设为选中 */
  const pendingSceneCodeCellSelectionRef = useRef<{ sceneId: string; sceneCode: string } | null>(null);

  // 进入分场 / userLastUsedSceneID 恢复选中 / 点击其它行：选中行出现在列表中后再居中滚动
  useEffect(() => {
    if (!effectiveBreakdownId || !selectedSceneId || scenes.length === 0) {
      return;
    }
    if (!scenes.some((s) => s.sceneID === selectedSceneId)) {
      return;
    }
    const key = `${effectiveBreakdownId}:${selectedSceneId}`;
    if (lastScrolledSelectionKeyRef.current === key) {
      return;
    }
    lastScrolledSelectionKeyRef.current = key;
    scrollSelectedScenesRowIntoView();
  }, [effectiveBreakdownId, selectedSceneId, scenes, scrollSelectedScenesRowIntoView]);

  // 场号编辑导致重排后：将目标行的场号单元格设置为选中态
  useEffect(() => {
    if (!setSelection) return;
    const pending = pendingSceneCodeCellSelectionRef.current;
    if (!pending) return;

    const normalizeSceneCode = (value: unknown): string => (value == null ? '' : String(value).trim());
    const pendingSceneId = pending.sceneId;
    const pendingSceneCode = pending.sceneCode;

    // 优先使用原 sceneId；若已由 temp 替换为真实 ID，则根据输入的场号精确匹配真实行
    let targetSceneId: string | null = null;
    if (scenes.some((s) => s.sceneID === pendingSceneId)) {
      targetSceneId = pendingSceneId;
    } else if (pendingSceneId.startsWith('temp-') && pendingSceneCode !== '') {
      const candidates = scenes.filter(
        (s) => !s.sceneID.startsWith('temp-') && normalizeSceneCode(s.sceneCode) === pendingSceneCode
      );
      if (candidates.length === 1) {
        targetSceneId = candidates[0].sceneID;
      } else if (
        selectedSceneId &&
        scenes.some(
          (s) =>
            s.sceneID === selectedSceneId &&
            normalizeSceneCode(s.sceneCode) === pendingSceneCode
        )
      ) {
        // 仅当 selectedSceneId 的场号与本次输入一致时才兜底，避免误选到旧行（如场号 1）
        targetSceneId = selectedSceneId;
      }
    }

    if (!targetSceneId) return;

    const rowIndex = scenes.findIndex((s) => s.sceneID === targetSceneId);
    if (rowIndex === -1) return;

    if (selectedSceneId !== targetSceneId) {
      setSelectedSceneId(targetSceneId);
      setSelectedSceneData(undefined);
    }

    setSelection({
      type: 'scenesTable',
      sceneID: targetSceneId,
      row: rowIndex,
      col: 0,
      colKey: 'sceneCode',
    });
    pendingSceneCodeCellSelectionRef.current = null;

    // 场号变更触发排序后居中该行（含已存在场次；selectedSceneId 未变时主 effect 不会去重拦截）
    if (effectiveBreakdownId) {
      lastScrolledSelectionKeyRef.current = `${effectiveBreakdownId}:${targetSceneId}`;
    }
    scrollSelectedScenesRowIntoView();
  }, [
    scenes,
    selectedSceneId,
    setSelection,
    setSelectedSceneId,
    setSelectedSceneData,
    effectiveBreakdownId,
    scrollSelectedScenesRowIntoView,
  ]);

  // 单元格编辑 Hook
  const {
    activateCellEdit,
    finishCellEdit,
  } = useSceneCellEdit(
    scenes,
    setSelectedSceneId,
    setSelectedSceneData,
    setActiveSceneId,
    setSetSearchVisible,
    setSetSearchText,
    setLocalSceneSetDisplay,
    setSetSearchPosition,
    setActiveLocationSceneId,
    setSceneLocationSearchVisible,
    setSceneLocationSearchText,
    setLocalSceneLocationDisplay,
    setSceneLocationSearchPosition,
    sceneLocationDisplayTexts,
    flushSceneUpdate
  );

  // 注意：以下代码已不再使用，因为现在使用 ExcelTable 的渲染方式
  // 创建统一的字段输入处理器（onChange、onBlur、ref）
  // 用于处理本地输入值存储和提交逻辑
  /*
  const createLocalFieldHandlers = useCallback((
    sceneId: string,
    fieldName: string,
    currentValue: string | null | undefined,
    onBlurExtra?: (finalValue: any) => void,
    valueTransform?: (value: string) => any
  ) => {
    // ... 旧代码已注释
  }, [localInputValues, handleSceneUpdate, flushSceneUpdate, scenes, finishCellEdit, createFieldBlurHandler]);
  */

  // 浮动组件位置更新 Hook
  useFloatingComponentPosition(
    setSearchVisible,
    activeSceneId,
    sceneLocationSearchVisible,
    activeLocationSceneId,
    setSetSearchPosition,
    setSceneLocationSearchPosition
  );

  // 场号验证 Hook（仅用于排序，不再检查重名）（使用内部的 setScenes）
  const {
    handleSceneCodeBlur,
  } = useSceneCodeValidation({
    setScenes: setScenes,
    sortConfig,
  });

  // 查找替换功能 Hook（使用内部的 setScenes）
  // 注意：查找替换功能暂时使用简化的 columns 配置
  const {
    findReplaceVisible,
    findReplaceColumn,
    findReplacePosition,
    handleOpenFindReplace,
    handleCloseFindReplace,
    handleFindReplaceUpdate,
    getColumnData
  } = useFindReplace(scenes, setScenes, setSceneLocationDisplayTexts);

  // 自动填充地点的函数（用于表格单元格和浮动 InputRecommend）
  const checkAndAutoFillLocation = useCallback((sceneId: string, sceneSetValue: string) => {
    const currentScene = scenes.find(s => s.sceneID === sceneId);
    if (!currentScene) return;
    
    // 检查拍摄地点是否为空（null、undefined或空字符串）
    const currentLocation = currentScene.sceneLocation;
    const isLocationEmpty = !currentLocation || currentLocation.trim() === '';
    
    // 如果拍摄地点为空且场景字段有值，自动填充
    if (isLocationEmpty && sceneSetValue && sceneSetValue.trim() !== '') {
      const autoLocation = `${t('breakdown.autoFillLocationPrefix')}${sceneSetValue.trim()}`;
      
      // 通过handleSceneUpdate更新，确保更新流程一致并触发同步
      handleSceneUpdate(sceneId, 'sceneLocation', autoLocation);
      
      // 同步地点显示文本缓存
      setSceneLocationDisplayTexts(prev => ({
        ...prev,
        [sceneId]: autoLocation
      }));
    }
  }, [scenes, handleSceneUpdate, setSceneLocationDisplayTexts, t]);

  // 处理表格行点击
  // 注意：暂时未使用，现在由 ExcelTable 处理
  // const handleRowClick = useCallback((record: SceneWithUserNames, rowIndex: number) => {
  //   return {
  //     onClick: (event: React.MouseEvent<HTMLTableRowElement>) => {
  //       // 选中场次
  //       setSelectedSceneId(record.sceneID);
  //       setSelectedSceneData(undefined); // 重置外部数据状态
  //     }
  //   };
  // }, [setSelectedSceneId, setSelectedSceneData]);

  // 注意：isPlaceholderRow 已在 callbacks 对象中内联定义，不再需要单独定义

  // ExcelTable 表格引用
  const tableRef = useRef<HTMLTableElement>(null);

  // 注意：旧的状态管理（useTableState、useKeyboardNavigation）已移除
  // 现在使用统一导航系统（由父组件 BreakdownMain 管理）

  // 存储内外、日夜、场景、拍摄地点字段的最新值（用于在 onCellEditEnd 中获取）
  const dropdownFieldValuesRef = useRef<Map<string, { field: string; value: any }>>(new Map());

  // 菜单状态管理（参考 InputRecommendNEW 的模式）
  const [interiorExteriorMenuVisible, setInteriorExteriorMenuVisible] = React.useState<Map<string, boolean>>(new Map());
  const [interiorExteriorMenuPosition, setInteriorExteriorMenuPosition] = React.useState<Map<string, MenuPosition>>(new Map());
  const [dayNightMenuVisible, setDayNightMenuVisible] = React.useState<Map<string, boolean>>(new Map());
  const [dayNightMenuPosition, setDayNightMenuPosition] = React.useState<Map<string, MenuPosition>>(new Map());

  // 计算菜单位置（表格场景，使用 fixed 定位）
  const calculateTableMenuPosition = React.useCallback((element: HTMLElement): MenuPosition | null => {
    const rect = element.getBoundingClientRect();
    const cellElement = element.closest('.excel-table-cell') as HTMLElement;
    let width = rect.width;
    
    if (cellElement) {
      const cellRect = cellElement.getBoundingClientRect();
      width = cellRect.width;
    }
    
    return {
      top: rect.bottom + 2, // 相对于视口顶部，与 InputRecommend 保持一致（2px 距离）
      left: rect.left, // 相对于视口左侧
      width: width
    };
  }, []);

  // 更新菜单位置（用于特定场景的菜单）
  const updateMenuPosition = React.useCallback((sceneId: string, fieldName: 'sceneInteriorExterior' | 'sceneDayNight') => {
    const buttonElement = document.querySelector(
      `button[data-scene-id="${sceneId}"][data-field="${fieldName}"]`
    ) as HTMLElement;
    
    if (buttonElement) {
      const position = calculateTableMenuPosition(buttonElement);
      if (position) {
        if (fieldName === 'sceneInteriorExterior') {
          setInteriorExteriorMenuPosition(prev => {
            const next = new Map(prev);
            next.set(sceneId, position);
            return next;
          });
        } else {
          setDayNightMenuPosition(prev => {
            const next = new Map(prev);
            next.set(sceneId, position);
            return next;
          });
        }
      }
    }
  }, [calculateTableMenuPosition]);

  // 监听编辑状态变化，更新菜单可见性和位置
  React.useEffect(() => {
    if (!currentStateAndPosition || currentStateAndPosition.type !== 'scenesTable') {
      // 如果不在编辑状态，关闭所有菜单
      setInteriorExteriorMenuVisible(new Map());
      setDayNightMenuVisible(new Map());
      return;
    }

    const { sceneID, colKey, state } = currentStateAndPosition;
    const isEditing = state === 'editing';

    // 如果退出编辑状态，关闭所有菜单
    if (!isEditing) {
      setInteriorExteriorMenuVisible(prev => {
        const next = new Map(prev);
        next.delete(sceneID);
        return next;
      });
      setDayNightMenuVisible(prev => {
        const next = new Map(prev);
        next.delete(sceneID);
        return next;
      });
      return;
    }

    // 如果进入编辑状态，根据 colKey 打开对应的菜单
    if (colKey === 'sceneInteriorExterior') {
      setInteriorExteriorMenuVisible(prev => {
        const next = new Map(prev);
        next.set(sceneID, true);
        return next;
      });
      // 延迟更新位置，确保 DOM 已渲染
      requestAnimationFrame(() => {
        updateMenuPosition(sceneID, 'sceneInteriorExterior');
      });
      // 关闭另一个菜单
      setDayNightMenuVisible(prev => {
        const next = new Map(prev);
        next.delete(sceneID);
        return next;
      });
    } else if (colKey === 'sceneDayNight') {
      setDayNightMenuVisible(prev => {
        const next = new Map(prev);
        next.set(sceneID, true);
        return next;
      });
      // 延迟更新位置，确保 DOM 已渲染
      requestAnimationFrame(() => {
        updateMenuPosition(sceneID, 'sceneDayNight');
      });
      // 关闭另一个菜单
      setInteriorExteriorMenuVisible(prev => {
        const next = new Map(prev);
        next.delete(sceneID);
        return next;
      });
    } else {
      // 如果编辑的是其他字段，关闭所有下拉菜单
      setInteriorExteriorMenuVisible(prev => {
        const next = new Map(prev);
        next.delete(sceneID);
        return next;
      });
      setDayNightMenuVisible(prev => {
        const next = new Map(prev);
        next.delete(sceneID);
        return next;
      });
    }
  }, [currentStateAndPosition, updateMenuPosition]);

  // 监听滚动事件，更新菜单位置
  React.useEffect(() => {
    const updateAllMenuPositions = () => {
      interiorExteriorMenuVisible.forEach((visible, sceneId) => {
        if (visible) {
          updateMenuPosition(sceneId, 'sceneInteriorExterior');
        }
      });
      dayNightMenuVisible.forEach((visible, sceneId) => {
        if (visible) {
          updateMenuPosition(sceneId, 'sceneDayNight');
        }
      });
    };

    const mainTableContainer = document.querySelector('.breakdown-table-container');
    const excelTableContainer = mainTableContainer?.querySelector('.excel-table-container') as HTMLElement;
    
    let rafId: number | null = null;
    const handleUpdate = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updateAllMenuPositions();
        rafId = null;
      });
    };

    if (mainTableContainer) {
      mainTableContainer.addEventListener('scroll', handleUpdate, { passive: true });
    }
    if (excelTableContainer) {
      excelTableContainer.addEventListener('scroll', handleUpdate, { passive: true });
    }

    let windowScrollTimer: ReturnType<typeof setTimeout> | null = null;
    const handleWindowEvents = () => {
      if (windowScrollTimer) clearTimeout(windowScrollTimer);
      windowScrollTimer = setTimeout(() => {
        handleUpdate();
      }, 4);
    };

    window.addEventListener('scroll', handleWindowEvents, { passive: true });
    window.addEventListener('resize', handleWindowEvents, { passive: true });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (windowScrollTimer) {
        clearTimeout(windowScrollTimer);
      }
      if (mainTableContainer) {
        mainTableContainer.removeEventListener('scroll', handleUpdate);
      }
      if (excelTableContainer) {
        excelTableContainer.removeEventListener('scroll', handleUpdate);
      }
      window.removeEventListener('scroll', handleWindowEvents);
      window.removeEventListener('resize', handleWindowEvents);
    };
  }, [interiorExteriorMenuVisible, dayNightMenuVisible, updateMenuPosition]);

  // 配置列
  const columns: TableColumn<SceneWithUserNames>[] = useMemo(() => {
    
    // 内外字段适配器组件
    const InteriorExteriorEditor: React.FC<any> = ({ value, onChange, onBlur, rowId, colKey, record, row, isEditing: isEditingProp }) => {
      const rowIndex = scenes.findIndex(s => s.sceneID === rowId);
      const buttonRef = React.useRef<HTMLButtonElement | null>(null);
      
      // 获取显示文本
      const getDisplayText = React.useCallback(() => {
        if (value === SceneInteriorExterior.INTERIOR) {
          return t('export.interiorShort');
        }
        if (value === SceneInteriorExterior.EXTERIOR) {
          return t('export.exteriorShort');
        }
        if (value === SceneInteriorExterior.INTERIOR_EXTERIOR) {
          return t('export.interiorExteriorShort');
        }
        return '\u00A0'; // 非断行空格
      }, [value]);

      // 打开菜单的函数（用于键盘导航）
      const openMenu = React.useCallback((element: HTMLElement) => {
        if (element === buttonRef.current) {
          updateMenuPosition(rowId, 'sceneInteriorExterior');
          setInteriorExteriorMenuVisible(prev => {
            const next = new Map(prev);
            next.set(rowId, true);
            return next;
          });
        }
      }, [rowId]);

      // 注册 ref 和 openMenu 方法
      React.useEffect(() => {
        const buttonElement = buttonRef.current;
        if (buttonElement && scenesTableElementRefs && rowIndex >= 0) {
          const columnKeys = ['sceneCode', 'sceneInteriorExterior', 'sceneDayNight', 'sceneSet', 'scenePlotDay', 'scenePagesNumber', 'scenePlot', 'sceneLocation', 'scenePeriod'];
          const colIndex = columnKeys.indexOf(colKey);
          if (colIndex >= 0) {
            const key = `${rowIndex}-${colIndex}`;
            scenesTableElementRefs.current.set(key, buttonElement);
            // 存储 openMenu 方法到元素属性（供键盘导航使用）
            (buttonElement as any).__openMenu = openMenu;
          }
        }
        return () => {
          if (buttonElement && scenesTableElementRefs && rowIndex >= 0) {
            const columnKeys = ['sceneCode', 'sceneInteriorExterior', 'sceneDayNight', 'sceneSet', 'scenePlotDay', 'scenePagesNumber', 'scenePlot', 'sceneLocation', 'scenePeriod'];
            const colIndex = columnKeys.indexOf(colKey);
            if (colIndex >= 0) {
              const key = `${rowIndex}-${colIndex}`;
              scenesTableElementRefs.current.delete(key);
            }
          }
        };
      }, [rowIndex, colKey, openMenu]);

      // 注意：菜单的打开/关闭由顶层的 useEffect 统一管理（监听 currentStateAndPosition）
      // 这里不再单独管理菜单状态，避免冲突
      
      return (
        <button
          ref={buttonRef}
          className="excel-cell-button"
          data-scene-id={rowId}
          data-field="sceneInteriorExterior"
          data-row-index={rowIndex}
          data-column-key={colKey}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditingProp) {
              // 如果不在编辑状态，点击应该由统一导航系统处理
              return;
            }
          }}
        >
          {getDisplayText()}
        </button>
      );
    };

    // 日夜字段适配器组件
    const DayNightEditor: React.FC<any> = ({ value, onChange, onBlur, rowId, colKey, record, row, isEditing: isEditingProp }) => {
      const rowIndex = scenes.findIndex(s => s.sceneID === rowId);
      const buttonRef = React.useRef<HTMLButtonElement | null>(null);
      
      // 获取显示文本
      const getDisplayText = React.useCallback(() => {
        if (value === SceneDayNight.MORNING) {
          return t('export.morningShort');
        }
        if (value === SceneDayNight.DAY) {
          return t('export.dayShort');
        }
        if (value === SceneDayNight.DUSK) {
          return t('export.duskShort');
        }
        if (value === SceneDayNight.NIGHT) {
          return t('export.nightShort');
        }
        return '\u00A0'; // 非断行空格
      }, [value]);

      // 打开菜单的函数（用于键盘导航）
      const openMenu = React.useCallback((element: HTMLElement) => {
        if (element === buttonRef.current) {
          updateMenuPosition(rowId, 'sceneDayNight');
          setDayNightMenuVisible(prev => {
            const next = new Map(prev);
            next.set(rowId, true);
            return next;
          });
        }
      }, [rowId]);

      // 注册 ref 和 openMenu 方法
      React.useEffect(() => {
        const buttonElement = buttonRef.current;
        if (buttonElement && scenesTableElementRefs && rowIndex >= 0) {
          const columnKeys = ['sceneCode', 'sceneInteriorExterior', 'sceneDayNight', 'sceneSet', 'scenePlotDay', 'scenePagesNumber', 'scenePlot', 'sceneLocation', 'scenePeriod'];
          const colIndex = columnKeys.indexOf(colKey);
          if (colIndex >= 0) {
            const key = `${rowIndex}-${colIndex}`;
            scenesTableElementRefs.current.set(key, buttonElement);
            // 存储 openMenu 方法到元素属性（供键盘导航使用）
            (buttonElement as any).__openMenu = openMenu;
          }
        }
        return () => {
          if (buttonElement && scenesTableElementRefs && rowIndex >= 0) {
            const columnKeys = ['sceneCode', 'sceneInteriorExterior', 'sceneDayNight', 'sceneSet', 'scenePlotDay', 'scenePagesNumber', 'scenePlot', 'sceneLocation', 'scenePeriod'];
            const colIndex = columnKeys.indexOf(colKey);
            if (colIndex >= 0) {
              const key = `${rowIndex}-${colIndex}`;
              scenesTableElementRefs.current.delete(key);
            }
          }
        };
      }, [rowIndex, colKey, openMenu]);

      // 注意：菜单的打开/关闭由顶层的 useEffect 统一管理（监听 currentStateAndPosition）
      // 这里不再单独管理菜单状态，避免冲突
      
      return (
        <button
          ref={buttonRef}
          className="excel-cell-button"
          data-scene-id={rowId}
          data-field="sceneDayNight"
          data-row-index={rowIndex}
          data-column-key={colKey}
          onClick={(e) => {
            e.stopPropagation();
            if (!isEditingProp) {
              // 如果不在编辑状态，点击应该由统一导航系统处理
              return;
            }
          }}
        >
          {getDisplayText()}
        </button>
      );
    };


    return [
      {
        key: 'sceneCode',
        title: <span>{t('breakdown.sceneNumberShort')}</span>,
        width: 100,
        className: 'breakdown-code-cell',
        sortable: true,
        sortType: 'sceneCode',
        showFindReplace: false,
        headerActions: canEdit && selectedSceneId && handleAddSceneInTable ? (
          <button 
            className="breakdown-btn-header-btn breakdown-add-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleAddSceneInTable();
            }}
            title={t('breakdown.addNewScene')}
            tabIndex={-1}
          >
            <AddIcon aria-label={t('common.add')} />
          </button>
        ) : undefined,
        editor: {
          type: 'text',
        },
      },
      {
        key: 'sceneInteriorExterior',
        title: t('breakdown.interiorExterior'),
        width: 36,
        className: 'breakdown-interior-exterior-cell',
        sortable: false,
        showFindReplace: false,
        editor: {
          type: 'custom',
          component: InteriorExteriorEditor,
        },
      },
      {
        key: 'sceneDayNight',
        title: t('breakdown.dayNightShort'),
        width: 36,
        className: 'breakdown-day-night-cell',
        sortable: false,
        showFindReplace: false,
        editor: {
          type: 'custom',
          component: DayNightEditor,
        },
      },
      {
        key: 'sceneSet',
        title: t('breakdown.set'),
        width: 188,
        className: 'breakdown-set-cell',
        sortable: true,
        sortType: 'text',
        showFindReplace: false,
        editor: {
          type: 'text',
        },
      },
      {
        key: 'scenePlotDay',
        title: t('breakdown.plotDay'),
        width: 56,
        className: 'breakdown-plot-day-cell',
        sortable: false,
        showFindReplace: canEdit,
        editor: {
          type: 'text',
        },
      },
      {
        key: 'scenePagesNumber',
        title: t('breakdown.scriptPages'),
        width: 38,
        className: 'breakdown-pages-cell',
        sortable: false,
        showFindReplace: false,
        editor: {
          type: 'text',
          inputMode: 'decimal',
        },
      },
      {
        key: 'scenePlot',
        title: t('breakdown.plot'),
        width: 210,
        className: 'breakdown-description-cell',
        sortable: false,
        showFindReplace: false,
        editor: {
          type: 'text',
        },
      },
      {
        key: 'sceneLocation',
        title: t('breakdown.shootingLocation'),
        width: 210,
        className: 'breakdown-location-cell',
        sortable: true,
        sortType: 'text',
        showFindReplace: canEdit,
        editor: {
          type: 'text',
        },
      },
      {
        key: 'scenePeriod',
        title: t('breakdown.shootingPeriodShort'),
        width: 66,
        className: 'breakdown-period-cell',
        sortable: true,
        sortType: 'text',
        showFindReplace: false,
        editor: {
          type: 'text',
        },
      },
    ];
  }, [selectedSceneId, scenes, handleAddSceneInTable, t, updateMenuPosition, scenesTableElementRefs, canEdit]);

  // 排序处理函数（使用内部的 setScenes）
  // 排序后选中行应保持为同一场次（如场号 43），而非同一行索引（如第 60 行）
  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortConfig({ key, direction });
    const sortEntry =
      effectiveBreakdownId != null && effectiveBreakdownId !== ''
        ? `${effectiveBreakdownId};${key};${direction}`
        : null;
    if (sortEntry) {
      setLocalOnlyField('userLastUsedScenesTableSort', sortEntry);
    }
    setScenes(prev => {
      const sorted = sortScenes(prev, key, direction);
      // 更新选中位置到新排序后的行索引，确保键盘导航和元素查找正确
      if (selectedSceneId && setSelection) {
        const newRowIndex = sorted.findIndex(s => s.sceneID === selectedSceneId);
        if (newRowIndex >= 0) {
          const colKey = currentStateAndPosition?.type === 'scenesTable' && currentStateAndPosition.colKey
            ? currentStateAndPosition.colKey
            : 'sceneCode';
          const col = currentStateAndPosition?.type === 'scenesTable' && currentStateAndPosition.col !== undefined
            ? currentStateAndPosition.col
            : 0;
          setSelection({
            type: 'scenesTable',
            sceneID: selectedSceneId,
            row: newRowIndex,
            col,
            colKey,
          });
        }
      }
      return sorted;
    });

    if (selectedSceneId) {
      scrollSelectedScenesRowIntoView();
    }
  }, [
    setSortConfig,
    setLocalOnlyField,
    effectiveBreakdownId,
    setScenes,
    selectedSceneId,
    setSelection,
    currentStateAndPosition,
    scrollSelectedScenesRowIntoView,
  ]);

  // 创建回调接口
  const callbacks: TableCallbacks<SceneWithUserNames> = useMemo(() => {
    return {
      // 获取单元格值
      getCellValue: (rowId: string, colKey: string) => {
        const scene = scenes.find(s => s.sceneID === rowId);
        if (!scene) return null;
        
        // 移除对 sceneLocation 的特殊处理，与 sceneSet 保持一致
        return (scene as any)[colKey];
      },

      // 获取单元格显示值（可能经过格式化）
      getCellDisplayValue: (rowId: string, colKey: string) => {
        const scene = scenes.find(s => s.sceneID === rowId);
        if (!scene) return null;
        
        // 内外列：显示映射值（内、外、内外）
        if (colKey === 'sceneInteriorExterior') {
          if (scene.sceneInteriorExterior === SceneInteriorExterior.INTERIOR) {
            return t('export.interiorShort');
          }
          if (scene.sceneInteriorExterior === SceneInteriorExterior.EXTERIOR) {
            return t('export.exteriorShort');
          }
          if (scene.sceneInteriorExterior === SceneInteriorExterior.INTERIOR_EXTERIOR) {
            return t('export.interiorExteriorShort');
          }
          return '';
        }
        
        // 日夜列：与导出/表格编辑统一短标签
        if (colKey === 'sceneDayNight') {
          if (scene.sceneDayNight === SceneDayNight.MORNING) {
            return t('export.morningShort');
          }
          if (scene.sceneDayNight === SceneDayNight.DAY) {
            return t('export.dayShort');
          }
          if (scene.sceneDayNight === SceneDayNight.DUSK) {
            return t('export.duskShort');
          }
          if (scene.sceneDayNight === SceneDayNight.NIGHT) {
            return t('export.nightShort');
          }
          return '';
        }
        
        // 页数列：统一显示1位小数
        if (colKey === 'scenePagesNumber') {
          return formatPageNumberDisplay(scene.scenePagesNumber);
        }
        
        // 移除对 sceneLocation 的特殊处理，与 sceneSet 保持一致
        return (scene as any)[colKey];
      },

      // 获取单元格样式（用于设置场号单元格的背景颜色）
      getCellStyle: (rowId: string, colKey: string) => {
        const scene = scenes.find(s => s.sceneID === rowId);
        if (!scene) return undefined;
        
        // 场号单元格需要特殊背景颜色
        if (colKey === 'sceneCode') {
          const backgroundColor = getSceneCodeCellBackgroundColor(
            scene.sceneInteriorExterior,
            scene.sceneDayNight
          );
          return {
            backgroundColor: backgroundColor !== 'transparent' ? backgroundColor : undefined,
          };
        }
        
        return undefined;
      },

      // 单元格值变化
      onCellValueChange: (rowId: string, colKey: string, value: any) => {
        if (!canEdit) return;
        handleSceneUpdate(rowId, colKey, value);
      },

      // 单元格进入编辑
      onCellEditStart: (rowId: string, colKey: string, rowIndex: number) => {
        if (!canEdit) return;
        // 对于内外、日夜字段，跳过 activateCellEdit（它们有自己的菜单打开逻辑）
        if (colKey === 'sceneInteriorExterior' || colKey === 'sceneDayNight') {
          // 标记开始编辑
          (window as any).__scenesTableIsEditing = true;
          // 选中行
          setSelectedSceneId(rowId);
          setSelectedSceneData(undefined);
          // 菜单会在组件挂载时自动打开（通过 useEffect）
          return;
        }
        
        // 标记开始编辑
        (window as any).__scenesTableIsEditing = true;
        
        // 调用现有的业务逻辑
        activateCellEdit(rowIndex, colKey);
        
        // 选中行
        setSelectedSceneId(rowId);
        setSelectedSceneData(undefined);
      },

      // 单元格完成编辑
      onCellEditEnd: async (rowId: string, colKey: string, value: any) => {
        if (!canEdit) return;
        const rowIndex = scenes.findIndex(s => s.sceneID === rowId);
        const scene = scenes.find(s => s.sceneID === rowId);
        
        // 对于内外、日夜、场景、拍摄地点字段，从全局 ref 获取最新值（确保使用选择的新值）
        let finalValue = value;
        if (colKey === 'sceneInteriorExterior' || colKey === 'sceneDayNight' || colKey === 'sceneSet' || colKey === 'sceneLocation') {
          const storedValue = dropdownFieldValuesRef.current.get(rowId);
          if (storedValue && storedValue.field === colKey) {
            finalValue = storedValue.value;
            // 使用后清除，避免影响后续编辑
            dropdownFieldValuesRef.current.delete(rowId);
          } else if (scene) {
            // 如果 ref 中没有值，尝试从状态获取
            finalValue = (scene as any)[colKey];
          }
        }
        
        // 对于内外、日夜字段，跳过 finishCellEdit（因为找不到元素）
        if (rowIndex >= 0 && colKey !== 'sceneInteriorExterior' && colKey !== 'sceneDayNight') {
          finishCellEdit(rowIndex, colKey);
        }
        
        // 特殊处理：剧情字段编辑时，若日历摘要为空则自动填充（需在 flush 前执行，确保一并写入缓存）
        if (colKey === 'scenePlot' && finalValue) {
          const currentScene = scenes.find(s => s.sceneID === rowId);
          const plotSummary = currentScene?.scenePlotSummary;
          if (!plotSummary || String(plotSummary).trim() === '') {
            handleSceneUpdate(rowId, 'scenePlotSummary', finalValue);
          }
        }
        
        try {
          // 先标记编辑完成，允许订阅者响应缓存更新
          (window as any).__scenesTableIsEditing = false;
          
          // 保存数据到缓存（这会同步触发订阅者）
          // 现在订阅者可以正常响应，因为 __scenesTableIsEditing 已经为 false
          // 对于内外、日夜、场景、拍摄地点字段，传递新值确保使用最新值
          if (colKey === 'sceneInteriorExterior' || colKey === 'sceneDayNight' || colKey === 'sceneSet' || colKey === 'sceneLocation') {
            await flushSceneUpdate(rowId, finalValue, colKey);
          } else {
            await flushSceneUpdate(rowId);
          }
          
          // 等待一下，确保订阅者更新完成
          await Promise.resolve();
        } catch (error) {
          // 如果更新失败，重新标记为编辑中（虽然不太可能，但为了安全）
          (window as any).__scenesTableIsEditing = true;
          throw error;
        }
        
        // 特殊处理：场景字段自动填充地点
        if (colKey === 'sceneSet' && value) {
          checkAndAutoFillLocation(rowId, value);
        }
        
        // 特殊处理：场号字段需要验证和排序
        if (colKey === 'sceneCode') {
          pendingSceneCodeCellSelectionRef.current = {
            sceneId: rowId,
            sceneCode: value == null ? '' : String(value).trim(),
          };
          handleSceneCodeBlur(rowId);
        }
      },

      // 行选择
      // 注意：只在切换到不同场景时才更新 selectedSceneId
      // 避免每次点击单元格都触发更新，导致不必要的重渲染
      onRowSelect: (rowId: string, rowIndex: number) => {
        if (selectedSceneId !== rowId) {
          setSelectedSceneId(rowId);
          setSelectedSceneData(undefined);
        }
      },

      // 判断占位行
      isPlaceholderRow: (rowId: string) => {
        return rowId.startsWith('temp-');
      },

      // 获取行样式
      getRowClassName: (rowId: string, rowIndex: number) => {
        if (rowId === selectedSceneId) {
          return 'breakdown-row-selected';
        }
        return undefined;
      },
    };
  }, [
    scenes,
    t,
    // 移除 sceneLocationDisplayTexts，因为不再在 getCellValue 中使用
    handleSceneUpdate,
    flushSceneUpdate,
    activateCellEdit,
    finishCellEdit,
    handleSceneCodeBlur,
    checkAndAutoFillLocation,
    selectedSceneId,
    canEdit,
    setSelectedSceneId,
    setSelectedSceneData,
  ]);


  // 注意：键盘导航已由统一导航系统处理（在父组件 BreakdownMain 中）

  // 处理单元格点击
  // 注意：状态管理已由统一导航系统处理，这里只保留业务逻辑回调
  const handleCellClick = useCallback((rowIndex: number, colIndex: number) => {
    const record = scenes[rowIndex];
    if (!record) return;

    const rowId = record.sceneID;
    const colKey = columns[colIndex]?.key;

    if (!colKey) return;

    // 调用业务逻辑回调
    callbacks.onRowSelect?.(rowId, rowIndex);
    callbacks.onCellClick?.(rowId, colKey, rowIndex, colIndex);
  }, [scenes, columns, callbacks]);

  // 处理单元格双击
  // 注意：状态管理已由统一导航系统处理，这里只保留业务逻辑回调
  const handleCellDoubleClick = useCallback((rowIndex: number, colIndex: number) => {
    const record = scenes[rowIndex];
    if (!record) return;

    const rowId = record.sceneID;
    const colKey = columns[colIndex]?.key;

    if (!colKey) return;

    // 调用业务逻辑回调
    callbacks.onCellEditStart(rowId, colKey, rowIndex);
    callbacks.onCellDoubleClick?.(rowId, colKey, rowIndex, colIndex);
  }, [scenes, columns, callbacks]);

  // 处理单元格值变化
  const handleCellValueChange = useCallback((rowIndex: number, colIndex: number, value: any) => {
    const record = scenes[rowIndex];
    if (!record) return;

    const rowId = record.sceneID;
    const colKey = columns[colIndex]?.key;

    if (!colKey) return;

    // 与自定义下拉对齐：onCellEditEnd 对 sceneSet/sceneLocation 会优先读该 ref（见 callbacks.onCellEditEnd）
    if (colKey === 'sceneSet' || colKey === 'sceneLocation') {
      dropdownFieldValuesRef.current.set(rowId, { field: colKey, value });
    }

    // 调用业务逻辑回调
    callbacks.onCellValueChange(rowId, colKey, value);
  }, [scenes, columns, callbacks]);

  // 处理单元格编辑完成
  // 注意：状态管理已由统一导航系统处理，这里只保留业务逻辑回调
  const handleCellEditEnd = useCallback(async (rowIndex: number, colIndex: number, value: any, skipSelection?: boolean) => {
    const record = scenes[rowIndex];
    if (!record) return;

    const rowId = record.sceneID;
    const colKey = columns[colIndex]?.key;

    if (!colKey) return;

    // 调用业务逻辑回调
    await callbacks.onCellEditEnd(rowId, colKey, value);
  }, [scenes, columns, callbacks]);

  // 从 td 元素获取字段位置（用于鼠标交互，当前未使用）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getCellPositionFromElement = useCallback((element: HTMLElement): CellPosition | null => {
    const td = element.closest('td');
    if (!td) return null;

    const row = td.getAttribute('data-row');
    const col = td.getAttribute('data-col');
    
    if (row !== null && col !== null) {
      return {
        row: parseInt(row, 10),
        col: parseInt(col, 10),
      };
    }
    
    return null;
  }, []);

  // 注意：鼠标交互（点击、双击）已由 TableCell 组件处理
  // 悬停状态由 useTableState 中的 setHoverCell 管理

  // 处理占位行点击外部完成输入
  useEffect(() => {
    // 检查是否有占位行
    const hasPlaceholder = scenes.some(s => s.sceneID.startsWith('temp-'));
    if (!hasPlaceholder) return;
    
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      // 检查是否点击在占位行的场号输入框外部
      const placeholderInput = document.querySelector('.excel-table-cell input[data-field="sceneCode"]') as HTMLInputElement;
      
      if (placeholderInput && document.activeElement === placeholderInput) {
        // 如果点击的不是输入框本身或其父元素，触发失焦完成输入
        if (!placeholderInput.contains(target) && !target.closest('.excel-table-cell')) {
          placeholderInput.blur();
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [scenes]);

  return (
    <div className="breakdown-table-container">
      {/* ExcelTable 内联渲染 */}
      <div className="excel-table-container breakdown-table-excel">
        <table ref={tableRef} className="excel-table">
          <thead>
            <tr>
              {columns.map((col, index) => {
                const isCurrentSort = sortConfig?.key === col.key;
                const sortDirection = isCurrentSort ? sortConfig.direction : null;
                
                return (
                  <th
                    key={col.key || index}
                    className={`excel-table-header ${col.className || ''}`}
                    style={{ width: col.width }}
                  >
                    <div className="excel-table-header-content">
                      <div className="excel-table-header-title">
                        <div className="excel-table-header-title-left">
                          {col.title}
                          {/* 排序按钮靠左贴近表头标题 */}
                          {col.sortable && handleSort && (
                            <button
                              className={`excel-sort-icon ${isCurrentSort ? 'active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                // 如果正在编辑单元格，不执行排序
                                if (currentStateAndPosition?.state === 'editing' && 
                                    currentStateAndPosition.type === 'scenesTable') {
                                  return;
                                }
                                const newDirection = isCurrentSort && sortDirection === 'asc' ? 'desc' : 'asc';
                                handleSort(col.key, newDirection);
                              }}
                              title={isCurrentSort 
                                ? (sortDirection === 'asc' ? '降序排列' : '升序排列')
                                : '排序'}
                              tabIndex={-1}
                            >
                              <SortIcon aria-label="排序" />
                            </button>
                          )}
                        </div>
                        {/* 右侧操作按钮：查找替换和额外的表头操作按钮 */}
                        {(col.showFindReplace && handleOpenFindReplace) || col.headerActions ? (
                          <div className="excel-table-header-title-right">
                            {/* 额外的表头操作按钮（如添加按钮） */}
                            {col.headerActions}
                            {/* 查找替换按钮 */}
                            {canEdit && col.showFindReplace && handleOpenFindReplace && (
                              <button
                                className={`excel-findreplace-btn ${findReplaceColumn === col.key ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenFindReplace(col.key, e);
                                }}
                                title="查找替换"
                                tabIndex={-1}
                              >
                                <FindReplaceIcon aria-label="查找替换" />
                              </button>
                            )}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {scenes.map((record, rowIndex) => {
              const rowId = record.sceneID;
              const rowClassName = callbacks.getRowClassName?.(rowId, rowIndex);
              const isPlaceholder = callbacks.isPlaceholderRow?.(rowId) || false;

              return (
                <tr
                  key={rowId}
                  className={`excel-table-row ${rowClassName || ''} ${isPlaceholder ? 'placeholder-row' : ''}`}
                  style={{ height: 24 }}
                >
                  {columns.map((col, colIndex) => {
                    // 使用统一导航系统获取单元格状态
                    const position: UnifiedPosition = {
                      type: 'scenesTable',
                      sceneID: rowId,
                      row: rowIndex,
                      col: colIndex,
                      colKey: col.key,
                    };
                    const cellState = getPositionState ? getPositionState(position) : 'normal';
                    const cellValue = callbacks.getCellValue(rowId, col.key);
                    const displayValue = callbacks.getCellDisplayValue
                      ? callbacks.getCellDisplayValue(rowId, col.key)
                      : undefined;
                    const cellStyle = callbacks.getCellStyle
                      ? callbacks.getCellStyle(rowId, col.key)
                      : undefined;
                    
                    // 检查是否处于编辑状态
                    const isEditing = currentStateAndPosition?.state === 'editing' &&
                      currentStateAndPosition.type === 'scenesTable' &&
                      currentStateAndPosition.sceneID === rowId &&
                      currentStateAndPosition.col === colIndex;

                    let cellInputRecommend: TableCellInputRecommendConfig | undefined;
                    if (isEditing && col.key === 'sceneSet') {
                      cellInputRecommend = {
                        fieldType: 'sceneSet',
                        sceneId: rowId,
                        rowIndex,
                        dataSource: scenes
                          .filter((s) => s.sceneBelongBreakdownID === record.sceneBelongBreakdownID)
                          .map((s) => s.sceneSet),
                        onAutoFillLocation: checkAndAutoFillLocation,
                      };
                    } else if (isEditing && col.key === 'sceneLocation') {
                      cellInputRecommend = {
                        fieldType: 'sceneLocation',
                        sceneId: rowId,
                        rowIndex,
                        dataSource: scenes
                          .filter((s) => s.sceneBelongBreakdownID === record.sceneBelongBreakdownID)
                          .map((s) => s.sceneLocation),
                      };
                    }

                    return (
                      <TableCell
                        key={col.key}
                        row={rowIndex}
                        col={colIndex}
                        value={cellValue}
                        displayValue={displayValue}
                        state={cellState}
                        editor={col.editor}
                        rowId={rowId}
                        colKey={col.key}
                        record={record}
                        isEditing={isEditing}
                        cellStyle={cellStyle}
                        className={col.className}
                        onCellClick={handleCellClick}
                        onCellDoubleClick={handleCellDoubleClick}
                        onCellValueChange={handleCellValueChange}
                        onCellEditEnd={handleCellEditEnd}
                        scenesTableElementRefs={scenesTableElementRefs}
                        inputRecommend={cellInputRecommend}
                      />
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* 底部空白区域，为编辑最后一行提供更好的视觉空间 */}
        <div className="breakdown-table-bottom-spacer">
          {canEdit && handleAddSceneInTableEnd && (
            <BottomAddButton 
              columns={columns}
              onAddSceneInTableEnd={handleAddSceneInTableEnd}
            />
          )}
        </div>
      </div>

      {/* 查找替换弹窗 - 使用 Portal 渲染到 body，避免随表格滚动 */}
      {findReplaceVisible && createPortal(
        <div 
          className="find-replace-wrapper" 
          style={{
            '--position-top': `${findReplacePosition.top}px`,
            '--position-left': `${findReplacePosition.left}px`,
          } as React.CSSProperties}
        >
          <FindReplace
            visible={findReplaceVisible}
            onClose={handleCloseFindReplace}
            columnData={getColumnData(findReplaceColumn)}
            columnIndex={findReplaceColumn}
            scenes={scenes}
            onUpdate={handleFindReplaceUpdate}
          />
        </div>,
        document.body
      )}

      {/* 下拉菜单 - 使用 Portal 渲染到 body，使用 fixed 定位 */}
      {scenes.map((scene) => {
        const sceneId = scene.sceneID;
        const interiorExteriorVisible = interiorExteriorMenuVisible.get(sceneId) || false;
        const interiorExteriorPos = interiorExteriorMenuPosition.get(sceneId);
        const dayNightVisible = dayNightMenuVisible.get(sceneId) || false;
        const dayNightPos = dayNightMenuPosition.get(sceneId);

        // 内外菜单项
        const interiorExteriorMenuItems = [
          { id: SceneInteriorExterior.INTERIOR, label: t('export.interiorShort') },
          { id: SceneInteriorExterior.EXTERIOR, label: t('export.exteriorShort') },
          { id: SceneInteriorExterior.INTERIOR_EXTERIOR, label: t('export.interiorExteriorShort') }
        ];

        // 日夜菜单项
        const dayNightMenuItems = [
          { id: SceneDayNight.MORNING, label: t('export.morningShort') },
          { id: SceneDayNight.DAY, label: t('export.dayShort') },
          { id: SceneDayNight.DUSK, label: t('export.duskShort') },
          { id: SceneDayNight.NIGHT, label: t('export.nightShort') }
        ];

        return (
          <React.Fragment key={sceneId}>
            {/* 内外下拉菜单 */}
            {interiorExteriorVisible && interiorExteriorPos && createPortal(
              <DropdownMenu
                items={interiorExteriorMenuItems}
                visible={interiorExteriorVisible}
                onClose={() => {
                  setInteriorExteriorMenuVisible(prev => {
                    const next = new Map(prev);
                    next.delete(sceneId);
                    return next;
                  });
                }}
                onSelect={(item) => {
                  const newValue = item.id as SceneInteriorExterior;
                  // 先关闭菜单（立即关闭，避免延迟）
                  setInteriorExteriorMenuVisible(prev => {
                    const next = new Map(prev);
                    next.delete(sceneId);
                    return next;
                  });
                  // 保存新值到全局 ref
                  dropdownFieldValuesRef.current.set(sceneId, { field: 'sceneInteriorExterior', value: newValue });
                  // 更新数据
                  handleSceneUpdate(sceneId, 'sceneInteriorExterior', newValue);
                  flushSceneUpdate(sceneId, newValue, 'sceneInteriorExterior');
                  // 触发 onBlur（通过查找对应的单元格并触发 blur）
                  const buttonElement = document.querySelector(
                    `button[data-scene-id="${sceneId}"][data-field="sceneInteriorExterior"]`
                  ) as HTMLElement;
                  if (buttonElement) {
                    buttonElement.blur();
                  }
                }}
                position={{
                  top: interiorExteriorPos.top,
                  left: interiorExteriorPos.left
                }}
                width={interiorExteriorPos.width}
                useFixedPosition={true}
                selectedValue={scene.sceneInteriorExterior || null}
              />,
              document.body
            )}

            {/* 日夜下拉菜单 */}
            {dayNightVisible && dayNightPos && createPortal(
              <DropdownMenu
                items={dayNightMenuItems}
                visible={dayNightVisible}
                onClose={() => {
                  setDayNightMenuVisible(prev => {
                    const next = new Map(prev);
                    next.delete(sceneId);
                    return next;
                  });
                }}
                onSelect={(item) => {
                  const newValue = item.id as SceneDayNight;
                  // 先关闭菜单（立即关闭，避免延迟）
                  setDayNightMenuVisible(prev => {
                    const next = new Map(prev);
                    next.delete(sceneId);
                    return next;
                  });
                  // 保存新值到全局 ref
                  dropdownFieldValuesRef.current.set(sceneId, { field: 'sceneDayNight', value: newValue });
                  // 更新数据
                  handleSceneUpdate(sceneId, 'sceneDayNight', newValue);
                  flushSceneUpdate(sceneId, newValue, 'sceneDayNight');
                  // 触发 onBlur（通过查找对应的单元格并触发 blur）
                  const buttonElement = document.querySelector(
                    `button[data-scene-id="${sceneId}"][data-field="sceneDayNight"]`
                  ) as HTMLElement;
                  if (buttonElement) {
                    buttonElement.blur();
                  }
                }}
                position={{
                  top: dayNightPos.top,
                  left: dayNightPos.left
                }}
                width={dayNightPos.width}
                useFixedPosition={true}
                selectedValue={scene.sceneDayNight || null}
              />,
              document.body
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// 底部添加按钮组件
function BottomAddButton({
  columns,
  onAddSceneInTableEnd
}: {
  columns: TableColumn<SceneWithUserNames>[];
  onAddSceneInTableEnd: (count: number) => void;
}) {
  // 找到场号列的位置和宽度
  const sceneCodeColumn = columns.find(col => col.key === 'sceneCode');
  if (!sceneCodeColumn) return null;

  const handleAddSceneInTableEnd = (count: number) => {
    console.log('BottomAddButton - handleAddSceneInTableEnd called with count:', count);
    onAddSceneInTableEnd(count);
  };

  return (
    <div className="breakdown-table-bottom-add-wrapper">
      <button 
        className="breakdown-table-bottom-add-btn breakdown-add-btn"
        onClick={() => handleAddSceneInTableEnd(1)}
        tabIndex={-1}
      >
        <AddIcon aria-label="添加" />
      </button>
    </div>
  );
}

export default ScenesTable;
